import {
  Loader2,
  Pause,
  ToggleLeft,
  ToggleRight,
  Volume2,
  VolumeX,
  ArrowLeft,
} from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  uploadVoiceFile,
  generateVoiceFromURLRequest,
  generateVoiceFromTextRequestWithSave,
} from "../../services/voices";
import {
  EMPTY_AUDIO_FILES,
  Entity,
  GeneratedAudio,
  Lang,
  Question,
  QuestionFormData,
  QuestionType,
} from "../../types";
import Header from "../Header";
import { translateText as apiTranslateText } from "../../services/translations";
import AudioControls from "./AudioControls";
import { getEntities } from "../../services/entities";
interface QuestionFormProps {
  question: Question | null;
  onSave: (questionData: Partial<Question>) => Promise<void>;
  onCancel: () => void;
  mode?: "edit" | "create" | "show";
  questionKind: "generic" | "specific";
}

type FormData = QuestionFormData;
export function QuestionForm({
  question,
  onSave,
  onCancel,
  setNotification,
  mode = "create",
  questionKind,
}: QuestionFormProps & { setNotification: (notification: { type: "success" | "error" | "warning" | "info"; title: string; message?: string }) => void }) {
  const navigate = useNavigate();
  const { user, translations, token, role: roleUser } = useAuth();
  const isViewingOrEditing = !!question;
  // Determine if the loaded question already has any audio (files or URLs) per language
  const initialHasAnyAudio = !!(
    question?.audioFiles?.es ||
    question?.audioFiles?.ca ||
    question?.audioFiles?.en ||
    question?.audioFiles?.ar ||
    (question?.audioUrls?.es && question?.audioUrls?.es.trim()) ||
    (question?.audioUrls?.ca && question?.audioUrls?.ca.trim()) ||
    (question?.audioUrls?.en && question?.audioUrls?.en.trim()) ||
    (question?.audioUrls?.ar && question?.audioUrls?.ar.trim())
  );
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const [showTranslateTitle, setShowTranslateTitle] = useState(false);
  const [showAudioUrlConvert, setShowAudioUrlConvert] = useState(false);
  const [loadingAudioVoice, setLoadingAudioVoice] = useState(false);
  const isEditing = !!question && mode === "edit";
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isValidAudio, setIsValidAudio] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loadingTitle, setLoadingTitle] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    text_es: question?.text.es || "",
    text_ca: question?.text.ca || "",
    text_en: question?.text.en || "",
    text_ar: question?.text.ar || "",
    audioUrl: question?.audioUrl || "",
    // Enable audio UI if the question already has any audio by language
    audioEnabled: initialHasAnyAudio,
    audioUrls: question?.audioUrls ?? { es: "", ca: "", en: "", ar: "" },
    audioObsKeys: question?.audioObsKeys ??
      question?.audioUrls ?? { es: "", ca: "", en: "", ar: "" },
    audioFiles: question?.audioFiles ?? {
      es: null,
      ca: null,
      en: null,
      ar: null,
    },
    type:
      questionKind === "generic" ? QuestionType.Generic : QuestionType.Custom,
    order: question?.order ?? 1,
    isGeneric: questionKind === "generic",
    surveyId: question?.surveyId || "",
    organizationId:
      question?.organizationId ||
      (user?.role === "Organization" ? user?.organizationId : ""),
  });
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [generatingAudioForLang, setGeneratingAudioForLang] = useState<{
    es: boolean;
    ca: boolean;
    en: boolean;
    ar: boolean;
  }>({ es: false, ca: false, en: false, ar: false });
  const [initialAudioGenerated, setInitialAudioGenerated] = useState<{
    es: boolean;
    ca: boolean;
    en: boolean;
    ar: boolean;
  }>({ es: false, ca: false, en: false, ar: false });

  const [needsAudioRegeneration, setNeedsAudioRegeneration] = useState<{
    es: boolean;
    ca: boolean;
    en: boolean;
    ar: boolean;
  }>({ es: false, ca: false, en: false, ar: false });
  const prevTextEs = useRef(formData.text_es);
  const prevTextCa = useRef(formData.text_ca);
  const prevTextEn = useRef(formData.text_en || "");
  const prevTextAr = useRef(formData.text_ar || "");
  
  // Refs para evitar llamadas duplicadas simultáneas
  const generatingAudioRefs = useRef<{
    es: boolean;
    ca: boolean;
    en: boolean;
    ar: boolean;
  }>({ es: false, ca: false, en: false, ar: false });
  const [playingAudio, setPlayingAudio] = useState<{ lang: string | null }>({
    lang: null,
  });
  const audioRefs = useRef<{
    es?: HTMLAudioElement | null;
    ca?: HTMLAudioElement | null;
    en?: HTMLAudioElement | null;
    ar?: HTMLAudioElement | null;
  }>({});

  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    if (
      token &&
      questionKind === "specific" &&
      user?.role === "Administrator"
    ) {
      setLoadingEntities(true);
      getEntities(token)
        .then(setEntities)
        .catch(() => setEntities([]))
        .finally(() => setLoadingEntities(false));
    }
  }, [token, questionKind, user?.role]);

  // Inicializar estados cuando se carga una pregunta existente
  useEffect(() => {
    if (question) {
      // Actualizar formData cuando la pregunta cambia (especialmente cuando se carga completa con audio)
      const hasAnyAudio = !!(
        question.audioFiles?.es ||
        question.audioFiles?.ca ||
        question.audioFiles?.en ||
        question.audioFiles?.ar ||
        (question.audioUrls?.es && question.audioUrls.es.trim()) ||
        (question.audioUrls?.ca && question.audioUrls.ca.trim()) ||
        (question.audioUrls?.en && question.audioUrls.en.trim()) ||
        (question.audioUrls?.ar && question.audioUrls.ar.trim())
      );

      setFormData({
        text_es: question.text.es || "",
        text_ca: question.text.ca || "",
        text_en: question.text.en || "",
        text_ar: question.text.ar || "",
        audioUrl: question.audioUrl || "",
        audioEnabled: hasAnyAudio,
        audioUrls: question.audioUrls ?? { es: "", ca: "", en: "", ar: "" },
        audioObsKeys: question.audioObsKeys ?? question.audioUrls ?? { es: "", ca: "", en: "", ar: "" },
        audioFiles: question.audioFiles ?? {
          es: null,
          ca: null,
          en: null,
          ar: null,
        },
        type: questionKind === "generic" ? QuestionType.Generic : QuestionType.Custom,
        order: question.order ?? 1,
        isGeneric: questionKind === "generic",
        surveyId: question.surveyId || "",
        organizationId: question.organizationId || (user?.role === "Organization" ? user?.organizationId : ""),
      });

      // Actualizar refs de texto previo
      prevTextEs.current = question.text.es || "";
      prevTextCa.current = question.text.ca || "";
      prevTextEn.current = question.text.en || "";
      prevTextAr.current = question.text.ar || "";

      // Inicializar initialAudioGenerated basado en audioUrls existentes
      setInitialAudioGenerated({
        es: !!question.audioUrls?.es,
        ca: !!question.audioUrls?.ca,
        en: !!question.audioUrls?.en,
        ar: !!question.audioUrls?.ar,
      });
      // No marcar como que necesita regeneración si ya tienen audio
      setNeedsAudioRegeneration({
        es: false,
        ca: false,
        en: false,
        ar: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question?.id, question?.audioFiles?.es, question?.audioFiles?.ca, question?.audioFiles?.en, question?.audioFiles?.ar]); // Actualizar cuando cambia la pregunta o se cargan los archivos de audio
  function ensureAudioFiles<
    T extends Partial<Record<Lang, File | null>> | undefined
  >(af: T): Record<Lang, File | null> {
    return { ...EMPTY_AUDIO_FILES, ...(af ?? {}) };
  }

  // Función para validar si el formulario es válido
  const isFormValid = (): boolean => {
    // Validar campos de texto obligatorios
    if (!formData.text_es?.trim()) {
      return false;
    }
    if (!formData.text_ca?.trim()) {
      return false;
    }
    if (!formData.text_ar?.trim()) {
      return false;
    }
    if (!formData.text_en?.trim()) {
      return false;
    }
    
    // Validar organizationId si es pregunta específica y el usuario es Administrator
    if (
      questionKind === "specific" &&
      user?.role === "Administrator" &&
      !formData.organizationId
    ) {
      return false;
    }

    // No permitir guardar si hay errores de validación
    if (Object.keys(formErrors).length > 0) {
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};
    if (!formData.text_es.trim()) {
      errors.text_es = translations.textSpanishRequired;
    }
    if (!formData.text_ca.trim()) {
      errors.text_ca = translations.textCatalanRequired;
    }
    if (!formData.text_ar?.trim()) {
      errors.text_ar = translations.textArabicRequired;
    }
    if (!formData.text_en?.trim()) {
      errors.text_en = translations.textEnglishRequired;
    }
    if (
      questionKind === "specific" &&
      user?.role === "Administrator" &&
      !formData.organizationId
    ) {
      errors.organizationId = translations.mustSelectEntity;
    }
    // if (formData?.surveyId === "0") {
    //   errors.surveyId = "Seleccionar una encuesta es obligatorio";
    // }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSaving(true);
    try {
      // Extraer solo audioUrls y audioFiles del spread, ya que contienen blob URLs y Files
      // que no deben enviarse al servidor
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { audioUrls: _audioUrls, audioFiles: _audioFiles, ...restFormData } = formData;
      
      await onSave({
        text: {
          es: formData.text_es,
          ca: formData.text_ca,
          en: formData.text_en || "",
          ar: formData.text_ar || "",
        },
        audioUrl: formData.audioUrl || undefined,
        // Enviar solo los objectKeys como audioUrls (no las URLs de blob)
        audioUrls: formData.audioObsKeys || { es: "", ca: "", en: "", ar: "" },
        audioObsKeys: formData.audioObsKeys || { es: "", ca: "", en: "", ar: "" },
        ...restFormData,
        id: question?.id,
        type:
          questionKind === "generic" ? QuestionType.Generic : QuestionType.Custom,
        isGeneric: questionKind === "generic",
        organizationId:
          questionKind === "specific"
            ? formData.organizationId || user?.organizationId
            : undefined,
      });
    } catch (error) {
      console.error("Error saving question:", error);
      setNotification({
        type: "error",
        title: translations.error,
        message: translations.audioGenerationFailed,
      });
    } finally {
      setIsSaving(false);
    }
  };

  async function translateText(text: string, to: string): Promise<string> {
    return await apiTranslateText({
      text,
      targetLanguage: to as "es" | "ca" | "en" | "ar",
      token: token!,
    });
  }
  /**
   * Función para implementar en otra tarea RED-43
   * @param lang
   * @returns
   */
  const handleFileUpload =
    (lang: "es" | "ca" | "en" | "ar") =>
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        const file = e.target.files[0];
        const audioUrl = URL.createObjectURL(file);
        const uploadedUrlOrKey = await uploadVoiceFile(file, token!);
        if (typeof uploadedUrlOrKey !== "string") {
          console.error("Error subiendo archivo de audio", uploadedUrlOrKey.message);
          return;
        }
        // uploadedUrlOrKey es el objectKey retornado por el servidor (ej: "voice/dcywlwax.mp3")
        setFormData((prev) => ({
          ...prev,
          audioFiles: { ...prev.audioFiles, [lang]: file },
          audioUrls: { ...prev.audioUrls, [lang]: audioUrl }, // URL de blob local para reproducir
          audioObsKeys: { ...(prev.audioObsKeys ?? {}), [lang]: uploadedUrlOrKey }, // objectKey del servidor
        }));
        setNeedsAudioRegeneration((prev) => ({ ...prev, [lang]: false }));
      }
    };

  /**
   *
   * @param audioUrl
   * @returns
   * Pensé en esta función para implementar en el futuro, en caso de que el usuario quiera genrar el audio desde una url
   */
  async function generateVoiceFromURL(audioUrl: string | undefined) {
    if (!audioUrl) return;
    try {
      setLoadingAudioVoice(true);
      const audioResult = await generateVoiceFromURLRequest(audioUrl, token!);

      if (audioResult instanceof Blob) {
        const audioObjectUrl = URL.createObjectURL(audioResult);
        setFormData((prev) => ({ ...prev, audioUrl: audioObjectUrl }));

        setNotification({
          type: "success",
          title: translations.audioGenerated,
          message: translations.audioGeneratedSuccessfully,
        });
      } else {
        console.error("ErrorResponse recibido:", audioResult);
        setNotification({
          type: "error",
          title: translations.error,
          message: audioResult.message,
        });
      }
    } catch (err) {
      console.error(err);
      setNotification({
        type: "error",
        title: translations.error,
        message: translations.couldNotGenerateAudio,
      });
    } finally {
      setLoadingAudioVoice(false);
    }
  }
  async function generateVoiceFromTextForForm(
    text: string,
    token: string,
    language: "es" | "ca" | "en" | "ar"
  ): Promise<GeneratedAudio & { objectKey: string }> {
    const { blob, file, objectKey } =
      (await generateVoiceFromTextRequestWithSave(text, token, language)) as {
        blob: Blob;
        file: File;
        objectKey: string;
      };
    const url = URL.createObjectURL(blob);
    setFormData((prev) => {
      const audioFiles = { ...(prev.audioFiles ?? {}) };
      const audioUrls = { ...(prev.audioUrls ?? {}) };
      const audioObsKeys = { ...(prev.audioObsKeys ?? {}) };
      audioFiles[language] = file;
      audioUrls[language] = url;
      audioObsKeys[language] = objectKey;
      return { ...prev, audioFiles, audioUrls, audioObsKeys };
    });

    return { blob, url, file, objectKey };
  }
  async function generateVoiceFromText(
    text: string,
    language: "es" | "ca" | "en" | "ar"
  ): Promise<{ url: string; file: File; objectKey: string }> {
    const res = await generateVoiceFromTextForForm(text, token!, language);
    return { url: res.url, file: res.file, objectKey: res.objectKey };
  }

  function setAudioForLang(
    lang: "es" | "ca" | "en" | "ar",
    file: File,
    objectKey?: string
  ) {
    setFormData((prev) => {
      const prevAF = ensureAudioFiles(prev.audioFiles);
      const next: FormData = {
        ...prev,
        audioFiles: { ...prevAF, [lang]: file },
        audioUrls: {
          ...(prev.audioUrls ?? {}),
          [lang]: URL.createObjectURL(file),
        },
      };
      if (objectKey) {
        next.audioObsKeys = { ...(prev.audioObsKeys ?? {}), [lang]: objectKey };
      }
      return next;
    });
  }

  const toggleAudio = (lang: "es" | "ca" | "en" | "ar") => {
    const currentAudio = audioRefs.current[lang];
    if (!currentAudio) return;

    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && audio !== currentAudio) audio.pause();
    });

    if (playingAudio.lang === lang) {
      currentAudio.pause();
      setPlayingAudio({ lang: null });
    } else {
      currentAudio
        .play()
        .then(() => setPlayingAudio({ lang }))
        .catch(() => {
          setNotification({
            type: "error",
            title: translations.error,
            message: translations.audioPlaybackFailed,
          });
        });
    }
  };

  const handleAudioToggleIcon = async () => {
    await handleAudioToggle({
      target: { checked: !formData.audioEnabled },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleAudioToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => {
      const enabled = !prev.audioEnabled;
      const updated: FormData = {
        ...prev,
        audioEnabled: enabled,
        audioUrls: enabled
          ? prev.audioUrls
          : { es: "", ca: "", en: "", ar: "" },
        audioFiles: enabled
          ? prev.audioFiles
          : { es: null, ca: null, en: null, ar: null },
        audioObsKeys: enabled
          ? prev.audioObsKeys
          : { es: "", ca: "", en: "", ar: "" },
      };

      if (
        enabled &&
        (!initialAudioGenerated.es ||
          !initialAudioGenerated.ca ||
          !initialAudioGenerated.en ||
          !initialAudioGenerated.ar ||
          typeof initialAudioGenerated !== "object") &&
        (prev.text_es || prev.text_ca)
      ) {
        setIsGeneratingAudio(true);

        const generate = async () => {
          const newInitialGenerated = {
            es: !!initialAudioGenerated.es,
            ca: !!initialAudioGenerated.ca,
            en: !!initialAudioGenerated.en,
            ar: !!initialAudioGenerated.ar,
          };

          // Generar audios uno por uno y actualizar estado incrementalmente
          const generateForLang = async (
            lang: "es" | "ca" | "en" | "ar",
            text: string
          ) => {
            // Verificar tanto initialAudioGenerated como si ya existe audio en formData
            const hasAudioAlready = !!(
              prev.audioUrls?.[lang] ||
              prev.audioFiles?.[lang]
            );
            
            // Verificar si ya se está generando usando el ref (evita condiciones de carrera)
            if (generatingAudioRefs.current[lang]) {
              return; // Ya se está generando, no hacer nada
            }
            
            if (!newInitialGenerated[lang] && !hasAudioAlready && text && text.trim()) {
              // Marcar como generando en ambos: estado y ref
              generatingAudioRefs.current[lang] = true;
              setGeneratingAudioForLang((prev) => ({ ...prev, [lang]: true }));
              try {
                const { file, objectKey } = await generateVoiceFromText(
                  text,
                  lang
                );
                setAudioForLang(lang, file, objectKey);
                newInitialGenerated[lang] = true;
                setInitialAudioGenerated((prev) => ({
                  ...prev,
                  [lang]: true,
                }));
              } catch (err) {
                console.error(`Error generating audio for ${lang}:`, err);
              } finally {
                generatingAudioRefs.current[lang] = false;
                setGeneratingAudioForLang((prev) => ({ ...prev, [lang]: false }));
              }
            } else if (hasAudioAlready && !newInitialGenerated[lang]) {
              // Si ya tiene audio pero initialAudioGenerated no está marcado, marcarlo
              setInitialAudioGenerated((prev) => ({
                ...prev,
                [lang]: true,
              }));
            }
          };

          // Generar en paralelo para mejor UX
          await Promise.all([
            generateForLang("es", prev.text_es),
            generateForLang("ca", prev.text_ca),
            generateForLang("en", prev.text_en || ""),
            generateForLang("ar", prev.text_ar || ""),
          ]);

          setIsGeneratingAudio(false);
        };

        generate().catch((err) => {
          console.error(err);
          setNotification({
            type: "error",
            title: translations.error,
            message: translations.audioGenerationFailed,
          });
          setIsGeneratingAudio(false);
        });
      }

      return updated;
    });
  };

  const handleTextBlur = async (lang: "es" | "ca" | "en" | "ar") => {
    const prevText =
      lang === "es"
        ? prevTextEs.current
        : lang === "ca"
        ? prevTextCa.current
        : lang === "en"
        ? prevTextEn.current
        : prevTextAr.current;
    const currentText = formData[`text_${lang}`];
    const textChanged = currentText?.trim() !== prevText?.trim();

    // Actualizar prevText antes de cualquier otra lógica para evitar loops
    if (lang === "es") {
      prevTextEs.current = currentText ?? "";

      if (currentText && currentText.trim() && mode !== "show") {
        setShowTranslateTitle(true);
      }
    } else if (lang === "ca") {
      prevTextCa.current = currentText ?? "";
    } else if (lang === "en") {
      prevTextEn.current = currentText ?? "";
    } else {
      prevTextAr.current = currentText ?? "";
    }

    // Si el audio está desactivado o no hay texto, no hacer nada más
    if (!formData.audioEnabled || !currentText?.trim()) {
      setNeedsAudioRegeneration((prev) => ({ ...prev, [lang]: false }));
      return;
    }

    const hasExistingAudio = !!(
      formData.audioUrls?.[lang] ||
      formData.audioFiles?.[lang]
    );

    // Si hay audio existente y el texto cambió, marcar para regeneración
    if (hasExistingAudio && textChanged) {
      setNeedsAudioRegeneration((prev) => ({ ...prev, [lang]: true }));
    }

    // Si NO hay audio existente, generarlo automáticamente
    // Solo generar si no está ya siendo generado (evitar duplicados)
    if (
      !hasExistingAudio &&
      currentText.trim() &&
      !generatingAudioForLang[lang] &&
      !generatingAudioRefs.current[lang]
    ) {
      // Marcar como generando en ambos: estado y ref
      generatingAudioRefs.current[lang] = true;
      setIsGeneratingAudio(true);
      setGeneratingAudioForLang((prev) => ({ ...prev, [lang]: true }));
      try {
        const { file, objectKey } = await generateVoiceFromText(
          currentText,
          lang
        );
        setAudioForLang(lang, file, objectKey);
        setInitialAudioGenerated((prev) => ({ ...prev, [lang]: true }));
        setNeedsAudioRegeneration((prev) => ({ ...prev, [lang]: false }));
      } catch (err) {
        setNotification({
          type: "error",
          title: translations.error,
          message: translations.audioGenerationFailed,
        });
      } finally {
        generatingAudioRefs.current[lang] = false;
        setIsGeneratingAudio(false);
        setGeneratingAudioForLang((prev) => ({ ...prev, [lang]: false }));
      }
    }
  };

  const regenerateAudio = async (lang: "es" | "ca" | "en" | "ar") => {
    const text = formData[`text_${lang}`];
    if (!text) return;
    
    // Verificar si ya se está generando
    if (generatingAudioRefs.current[lang] || generatingAudioForLang[lang]) {
      return; // Ya se está generando, no hacer nada
    }
    
    // Si está reproduciendo este idioma, detenerlo primero
    if (playingAudio.lang === lang) {
      const currentAudio = audioRefs.current[lang];
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
      setPlayingAudio({ lang: null });
    }
    
    try {
      generatingAudioRefs.current[lang] = true;
      setIsGeneratingAudio(true);
      setGeneratingAudioForLang((prev) => ({ ...prev, [lang]: true }));
      const { file, objectKey } = await generateVoiceFromText(text, lang);
      setAudioForLang(lang, file, objectKey);
      setNeedsAudioRegeneration((prev) => ({ ...prev, [lang]: false }));
      if (lang === "es") prevTextEs.current = text;
      else if (lang === "ca") prevTextCa.current = text;
      else if (lang === "en") prevTextEn.current = text;
      else prevTextAr.current = text;
    } catch {
      setNotification({
        type: "error",
        title: translations.error,
        message: translations.audioGenerationFailed,
      });
    } finally {
      generatingAudioRefs.current[lang] = false;
      setIsGeneratingAudio(false);
      setGeneratingAudioForLang((prev) => ({ ...prev, [lang]: false }));
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Modal de guardado */}
      {isSaving && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-sm w-full mx-4">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
              <p className="text-lg font-medium text-gray-900">
                {translations.savingQuestion}
              </p>
              <p className="text-sm text-gray-500 text-center">
                {translations.saving}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <Header>
        <div className="flex items-center gap-4">
          {isViewingOrEditing && (
            <button
              onClick={() => {
                // Navegar a la página de questions correspondiente
                const path = questionKind === "generic" ? "/questions" : "/questions-specific";
                navigate(path);
              }}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-gray-700"
              title={translations.back || "Volver"}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {question
                ? translations.editQuestion
                : questionKind === "generic"
                ? translations.newGenericQuestion
                : translations.newSpecificQuestion}
            </h1>
            <p className="text-gray-600 mt-1">
              {question
                ? translations.modifyExistingQuestion
                : questionKind === "generic"
                ? translations.createNewGenericQuestion
                : translations.createNewSpecificQuestion}
            </p>
          </div>
        </div>
      </Header>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {questionKind === "specific" && user?.role === "Administrator" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {translations.selectEntity} *
                </label>

                {loadingEntities ? (
                  <p className="text-sm text-gray-500">
                    {translations.loading}
                  </p>
                ) : entities.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {translations.noEntitiesAvailable}
                  </p>
                ) : (
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.organizationId
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={formData.organizationId ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        organizationId: e.target.value,
                      }))
                    }
                    disabled={mode === "show" || isSaving}
                  >
                    <option value="">{translations.selectEntity}</option>
                    {entities.map((ent) => (
                      <option key={ent.id} value={ent.id}>
                        {ent.name}
                      </option>
                    ))}
                  </select>
                )}

                {formErrors.organizationId && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.organizationId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Question Text */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.questionText} ({translations.spanish}) *
              </label>
              <textarea
                disabled={isGeneratingAudio || isSaving}
                rows={3}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.text_es
                    ? "border-red-500"
                    : "hover:border-gray-400 border-gray-300"
                }`}
                value={formData.text_es}
                onChange={(e) => {
                  setFormData({ ...formData, text_es: e.target.value });
                  if (formErrors.text_es) {
                    setFormErrors((prev) => ({ ...prev, text_es: "" }));
                  }
                }}
                onBlur={() => handleTextBlur("es")}
                placeholder={translations.writeQuestionInSpanish}
              />
              {formErrors.text_es && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.text_es}
                </p>
              )}
              {showTranslateTitle && !loadingTitle && mode !== "show" && (
                <button
                  type="button"
                  onClick={async () => {
                    setLoadingTitle(true);
                    
                    const [toCa, toEn, toAr] = await Promise.all([
                      translateText(formData.text_es, "ca"),
                      translateText(formData.text_es, "en"),
                      translateText(formData.text_es, "ar"),
                    ]);

                    setFormData((prev) => ({
                      ...prev,
                      text_ca: toCa,
                      text_en: toEn,
                      text_ar: toAr,
                    }));

                    prevTextCa.current = toCa;
                    prevTextEn.current = toEn;
                    prevTextAr.current = toAr;

                    // Generar o regenerar audios automáticamente si está habilitado
                    if (formData.audioEnabled) {
                      // Generar/regenerar audios en paralelo para TODOS los idiomas (incluido español)
                      const generateOrRegenerateAudioForLang = async (
                        lang: "es" | "ca" | "en" | "ar",
                        text: string
                      ) => {
                        // Verificar si ya se está generando usando el ref (evita condiciones de carrera)
                        if (generatingAudioRefs.current[lang]) {
                          return; // Ya se está generando, no hacer nada
                        }

                        // Si hay texto, generar o regenerar el audio automáticamente
                        if (text.trim()) {
                          generatingAudioRefs.current[lang] = true;
                          setGeneratingAudioForLang((prev) => ({
                            ...prev,
                            [lang]: true,
                          }));
                          
                          // Si está reproduciendo este idioma, detenerlo primero
                          if (playingAudio.lang === lang) {
                            const currentAudio = audioRefs.current[lang];
                            if (currentAudio) {
                              currentAudio.pause();
                              currentAudio.currentTime = 0;
                            }
                            setPlayingAudio({ lang: null });
                          }
                          
                          try {
                            const { file, objectKey } =
                              await generateVoiceFromText(text, lang);
                            setAudioForLang(lang, file, objectKey);
                            setInitialAudioGenerated((prevIG) => ({
                              ...prevIG,
                              [lang]: true,
                            }));
                            // Limpiar el flag de regeneración ya que acabamos de regenerar
                            setNeedsAudioRegeneration((prev) => ({
                              ...prev,
                              [lang]: false,
                            }));
                          } catch (err) {
                            console.error(`Error generating audio for ${lang}:`, err);
                          } finally {
                            generatingAudioRefs.current[lang] = false;
                            setGeneratingAudioForLang((prev) => ({
                              ...prev,
                              [lang]: false,
                            }));
                          }
                        }
                      };

                      await Promise.all([
                        generateOrRegenerateAudioForLang("es", formData.text_es),
                        generateOrRegenerateAudioForLang("ca", toCa),
                        generateOrRegenerateAudioForLang("en", toEn),
                        generateOrRegenerateAudioForLang("ar", toAr),
                      ]);
                    }

                    setLoadingTitle(false);
                    setShowTranslateTitle(false);
                  }}
                    disabled={loadingTitle || isSaving}
                  className="flex items-center space-x-2 text-blue-600 text-sm hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingTitle ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{translations.translating}</span>
                    </>
                  ) : (
                    <span>{translations.generateTranslation}</span>
                  )}
                </button>
              )}
              
              {/* Indicadores de progreso */}
              {loadingTitle && (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center space-x-2 text-xs text-gray-600">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>{translations.translating}</span>
                  </div>
                  {formData.audioEnabled && (
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>{translations.generatingAudios}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.questionText} ({translations.catalan}) *
              </label>
              <textarea
                disabled={isGeneratingAudio || isSaving}
                rows={3}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.text_ca
                    ? "border-red-500"
                    : "hover:border-gray-400  border-gray-300"
                }`}
                value={formData.text_ca}
                onChange={(e) =>
                  setFormData({ ...formData, text_ca: e.target.value })
                }
                placeholder={translations.writeQuestionInCatalan}
                onBlur={() => handleTextBlur("ca")}
              />
              {formErrors.text_ca && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.text_ca}
                </p>
              )}
            </div>
            {/* Inglés (opcional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.questionText} ({translations.english ?? "English"}
                )
              </label>
              <textarea
                disabled={isGeneratingAudio || isSaving}
                rows={3}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.text_en
                    ? "border-red-500"
                    : "hover:border-gray-400 border-gray-300"
                }`}
                value={formData.text_en}
                onChange={(e) =>
                  setFormData({ ...formData, text_en: e.target.value })
                }
                onBlur={() => handleTextBlur("en")}
                placeholder={
                  (translations.writeQuestionInEnglish as string) ??
                  "Write the question in English"
                }
              />
              {formErrors.text_en && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.text_en}
                </p>
              )}
            </div>

            {/* Árabe (opcional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.questionText} ({translations.arabic ?? "العربية"})
              </label>
              <textarea
                disabled={isGeneratingAudio || isSaving}
                rows={3}
                dir="rtl"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.text_ar
                    ? "border-red-500"
                    : "hover:border-gray-400 border-gray-300"
                }`}
                value={formData.text_ar ?? ""}
                onChange={(e) =>
                  setFormData({ ...formData, text_ar: e.target.value })
                }
                onBlur={() => handleTextBlur("ar")}
                placeholder={
                  (translations.writeQuestionInArabic as string) ??
                  "اكتب السؤال بالعربية"
                }
              />
              {formErrors.text_ar && (
                <p className="mt-1 text-sm text-red-600">
                  {formErrors.text_ar}
                </p>
              )}
            </div>
          </div>

          {/* Sección de Audio */}
          <div className="grid grid-cols-1 gap-6">
            <div>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  disabled={isGeneratingAudio || isSaving}
                  onClick={handleAudioToggleIcon}
                  className={`${
                    isGeneratingAudio
                      ? "text-gray-500"
                      : formData.audioEnabled
                      ? "text-blue-600 hover:text-blue-800"
                      : "text-red-500 hover:text-red-700"
                  }`}
                >
                  {isGeneratingAudio ? (
                    <Loader2 className="animate-spin w-5 h-5 text-gray-500" />
                  ) : formData.audioEnabled ? (
                    <ToggleRight />
                  ) : (
                    <ToggleLeft />
                  )}
                </button>
                <span className="text-sm font-medium text-gray-700">
                  {translations.audioEnabled}
                </span>
              </div>

              <p className="text-xs text-gray-500 mt-1">
                {translations.audioEnabledDescription}
              </p>
            </div>
            {isGeneratingAudio && (
              <p className="text-xs text-gray-500 mt-1">
                {translations.generatingAudio}
              </p>
            )}
            {formData.audioEnabled && (
              <div className="mt-2">
                {(["es", "ca", "en", "ar"] as const).map((lng) => (
                  <AudioControls
                    key={lng}
                    lang={lng}
                    formData={formData}
                    setFormData={setFormData}
                    playingAudio={playingAudio}
                    needsAudioRegeneration={needsAudioRegeneration}
                    prevTextEs={prevTextEs}
                    prevTextCa={prevTextCa}
                    prevTextEn={prevTextEn}
                    prevTextAr={prevTextAr}
                    toggleAudio={toggleAudio}
                    regenerateAudio={regenerateAudio}
                    handleFileUpload={handleFileUpload}
                    audioRefs={audioRefs}
                    setPlayingAudio={setPlayingAudio}
                    isGeneratingAudio={generatingAudioForLang[lng]}
                  />
                ))}
              </div>
            )}
          </div>
          {/* Audio URL y Orden */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* <div> */}
            {/* <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.audioUrl}
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.audioUrl}
                onChange={(e) =>
                  setFormData({ ...formData, audioUrl: e.target.value })
                }
                onBlur={async () => {
                  // if (formData.audioUrl && formData.audioUrl.trim())
                  // setShowAudioUrlConvert(true);
                }}
                placeholder="https://ejemplo.com/audio.mp3"
              />
              <p className="text-xs text-gray-500 mt-1">
                {translations.audioUrlDescription}
              </p> */}
            {/*showAudioUrlConvert && !loadingAudioVoice && mode !== "show" && (
                <p
                  className="text-blue-600 text-sm mt-1 cursor-pointer hover:underline"
                  onClick={async () => {
                    setLoadingAudioVoice(true);
                    const voice = await generateVoiceFromURL(formData.audioUrl);
                    setLoadingAudioVoice(false);
                    setShowAudioUrlConvert(false);
                  }}
                >
                  Generar audio automáticamente
                </p>
              )}
              {loadingAudioVoice && (
                <p className="text-sm text-gray-500 mt-1">Traduciendo...</p>
              )*/}
            {/* </div> */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {translations.questionOrder} *
              </label>
              <input
                type="number"
                min="1"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  formErrors.order ? "border-red-500" : "border-gray-300"
                }`}
                value={formData.order}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    order: parseInt(e.target.value) || 1,
                  });
                  if (formErrors.order) {
                    setFormErrors((prev) => ({ ...prev, order: "" }));
                  }
                }}
                placeholder="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {translations.questionOrderDescription}
              </p>
              {formErrors.order && (
                <p className="mt-1 text-sm text-red-600">{formErrors.order}</p>
              )}
            </div>*/}
          </div>

          {/* Preview */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              {translations.preview}
            </h3>
            <div className="space-y-4">
              {(["es", "ca", "en", "ar"] as const).map((lang) => {
                const text = formData[`text_${lang}`];
                const playUrl = formData.audioUrls?.[lang];
                const isLangPlaying = playingAudio.lang === lang;
                const langLabel =
                  lang === "es"
                    ? (translations.spanish as string)
                    : lang === "ca"
                    ? (translations.catalan as string)
                    : lang === "en"
                    ? (translations.english as string) ?? "English"
                    : (translations.arabic as string) ?? "العربية";

                return (
                  <div key={lang} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-700">
                        {langLabel}:
                      </p>
                      {formData.audioEnabled && text ? (
                        playUrl ? (
                          <button
                            type="button"
                            onClick={() => toggleAudio(lang)}
                            className="hover:text-blue-800 text-blue-600 p-1 rounded-full hover:bg-blue-100 transition"
                          >
                            {isLangPlaying ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Volume2 className="w-4 h-4" />
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm flex items-center space-x-1">
                            <VolumeX className="w-4 h-4" />
                            <span>{translations.noAudioAvailable}</span>
                          </span>
                        )
                      ) : null}
                    </div>

                    <p className="text-gray-900 text-sm">
                      {text || (
                        <span className="text-gray-400">
                          {translations.noText}
                        </span>
                      )}
                    </p>

                    {formData.audioEnabled && playUrl && text && (
                      <audio
                        ref={(el) => (audioRefs.current[lang] = el)}
                        src={playUrl}
                        onEnded={() => setPlayingAudio({ lang: null })}
                        onError={() =>
                          setNotification({
                            type: "error",
                            title: translations.error,
                            message: translations.audioPlaybackFailed,
                          })
                        }
                        className="hidden"
                      />
                    )}
                  </div>
                );
              })}

              <div className="flex items-center space-x-2 mt-4">
                <span className="text-xs text-gray-500">
                  {translations.ratingScale}:
                </span>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <div
                      key={rating}
                      className="w-6 h-6 rounded-full border-2 border-blue-300 flex items-center justify-center text-xs font-medium text-blue-600"
                    >
                      {rating}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {translations.cancel}
            </button>
            <button
              type="submit"
              disabled={isSaving || isGeneratingAudio || !isFormValid() || mode === "show"}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{translations.saving}</span>
                </>
              ) : (
                <span>{translations.save}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
